import React, { useState, useEffect } from 'react';
import { Step } from 'react-joyride';
import OnboardingTour from './OnboardingTour';
import { useOnboarding } from '../../hooks/useOnboarding';
import { useLanguage } from '../../contexts/LanguageContext';

interface DocumentStorageTourProps {
  forceShow?: boolean;
}

const DocumentStorageTour: React.FC<DocumentStorageTourProps> = ({ forceShow = false }) => {
  const { showTour: autoShowTour, completeTour, skipTour } = useOnboarding('document-storage');
  const [showTour, setShowTour] = useState(false);
  const { t } = useLanguage();

  // Auto-show only if user hasn't completed/skipped the tour, OR if manually triggered
  useEffect(() => {
    if (autoShowTour || forceShow) {
      setShowTour(true);
    }
  }, [autoShowTour, forceShow]);

  const steps: Step[] = [
    {
      target: 'body',
      content: (
        <div>
          <h3 className="text-2xl font-bold mb-3 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            📁 Üdvözöl a Dokumentumtár!
          </h3>
          <p className="text-gray-600 dark:text-gray-400 text-base leading-relaxed">
            Központi helyen tárolhatod és kezelheted az összes dokumentumodat. Mappák, címkék, kategóriák segítségével könnyen megtalálod, amire szükséged van.
          </p>
          <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
            <p className="text-sm text-blue-700 dark:text-blue-300">
              💡 <strong>Tipp:</strong> Használd a nyilakat a navigáláshoz!
            </p>
          </div>
        </div>
      ),
      placement: 'center',
      disableBeacon: true,
    },
    {
      target: 'body',
      content: (
        <div>
          <h3 className="text-xl font-bold mb-2 flex items-center gap-2">
            📤 Dokumentum Feltöltés
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-3">
            Új dokumentumok feltöltéséhez használd a "Feltöltés" vagy "Új dokumentum" gombokat. Támogatott formátumok: PDF, Word, Excel, képek és még sok más!
          </p>
          <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
            <li className="flex items-start gap-2">
              <span className="text-green-500">✓</span>
              <span>Drag & drop támogatás</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-500">✓</span>
              <span>Több fájl egyszerre</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-500">✓</span>
              <span>Automatikus kategorizálás</span>
            </li>
          </ul>
        </div>
      ),
      placement: 'center',
    },
    {
      target: '.folder-tree',
      content: (
        <div>
          <h3 className="text-xl font-bold mb-2 flex items-center gap-2">
            📂 Mappa Struktúra
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-3">
            Rendszerezd dokumentumaidat mappákba és almappákba. Hierarchikus struktúra könnyű navigációval.
          </p>
          <div className="p-3 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-lg">
            <p className="text-sm text-purple-700 dark:text-purple-300">
              <strong>Pro tipp:</strong> Jobb klikk a mappán új almappa létrehozásához!
            </p>
          </div>
        </div>
      ),
      placement: 'right',
    },
    {
      target: '.filter-panel',
      content: (
        <div>
          <h3 className="text-xl font-bold mb-2 flex items-center gap-2">
            🔍 Intelligens Szűrő Panel
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-3">
            Hatékony keresés és szűrés dokumentumok között. Kombinálj több szűrőt a pontos találatokért!
          </p>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded">
              <span className="font-semibold">🔍 Keresés</span>
            </div>
            <div className="p-2 bg-green-50 dark:bg-green-900/20 rounded">
              <span className="font-semibold">🏷️ Címkék</span>
            </div>
            <div className="p-2 bg-purple-50 dark:bg-purple-900/20 rounded">
              <span className="font-semibold">📁 Kategória</span>
            </div>
            <div className="p-2 bg-amber-50 dark:bg-amber-900/20 rounded">
              <span className="font-semibold">📅 Időszak</span>
            </div>
          </div>
          <div className="mt-3 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg">
            <p className="text-sm text-blue-700 dark:text-blue-300">
              <strong>💡 Tipp:</strong> A címkék mérete mutatja, hogy mennyire gyakran használtak!
            </p>
          </div>
        </div>
      ),
      placement: 'bottom',
    },
    {
      target: '.view-mode-toggle',
      content: (
        <div>
          <h3 className="text-xl font-bold mb-2 flex items-center gap-2">
            🎨 Nézet Váltás
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-3">
            Válts lista és rács nézet között. A rács nézet előnézeti képeket mutat, a lista részletesebb információkat.
          </p>
          <div className="flex items-center gap-3 text-sm">
            <div className="flex items-center gap-2">
              <span className="text-2xl">📋</span>
              <span className="text-gray-600 dark:text-gray-400">Lista</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-2xl">🎴</span>
              <span className="text-gray-600 dark:text-gray-400">Rács</span>
            </div>
          </div>
        </div>
      ),
      placement: 'bottom',
    },
    {
      target: '.document-panel-header',
      content: (
        <div>
          <h3 className="text-xl font-bold mb-2 flex items-center gap-2">
            📄 Dokumentum Panel
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-3">
            Itt láthatod a dokumentumokat. A header-ben található a nézet váltás (lista/rács) és az új dokumentum/mappa gombok.
          </p>
          <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
            <li className="flex items-start gap-2">
              <span className="text-blue-500">✓</span>
              <span>Nézet váltás: lista vagy rács</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-500">✓</span>
              <span>Új mappa vagy dokumentum létrehozása</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-purple-500">✓</span>
              <span>Feltöltés gomb a gyors hozzáadáshoz</span>
            </li>
          </ul>
        </div>
      ),
      placement: 'bottom',
    },
    {
      target: 'body',
      content: (
        <div>
          <h3 className="text-xl font-bold mb-2 flex items-center gap-2">
            🏷️ Címke Felhő
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-3">
            Vizuális címke böngésző a szűrő panelen belül. A színes címkék segítenek gyorsan szűrni a dokumentumokat.
          </p>
          <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
            <li className="flex items-start gap-2">
              <span className="text-blue-500">✓</span>
              <span>Kattints egy címkére a szűréshez</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-500">✓</span>
              <span>Több címke is kiválasztható</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-purple-500">✓</span>
              <span>A szám mutatja, hány dokumentum van</span>
            </li>
          </ul>
        </div>
      ),
      placement: 'center',
    },
    {
      target: 'body',
      content: (
        <div>
          <h3 className="text-xl font-bold mb-2 flex items-center gap-2">
            🎉 Készen állsz!
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Most már tudod, hogyan használd a Dokumentumtárat! A dokumentumok a jobb oldali panelben jelennek meg lista vagy rács nézetben.
          </p>
          <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-lg border border-green-200 dark:border-green-800">
            <p className="text-sm text-green-700 dark:text-green-300 mb-2">
              <strong>💡 Következő lépések:</strong>
            </p>
            <ul className="space-y-1 text-sm text-green-600 dark:text-green-400">
              <li>• Kattints egy mappára a bal oldali fában</li>
              <li>• Hozz létre új mappákat és almappákat</li>
              <li>• Tölts fel dokumentumokat</li>
              <li>• Adj hozzá címkéket a könnyebb kereséshez</li>
            </ul>
          </div>
        </div>
      ),
      placement: 'center',
    },
  ];

  const handleComplete = () => {
    setShowTour(false);
    completeTour();
  };

  const handleSkip = () => {
    setShowTour(false);
    skipTour();
  };

  return (
    <OnboardingTour
      steps={steps}
      run={showTour}
      onComplete={handleComplete}
      onSkip={handleSkip}
    />
  );
};

export default DocumentStorageTour;
